<template>
  <section class="ugc-pop-art-control">
    <ugc-crop-box
      :fabric-canvas="this.fabricCanvas"
      :temp-crop-data="this.tempCropData"
      :lock-square="this.lockSquare"
      :image-format="this.imageFormat"
      ref="cropbox"
    />
    <p class="ugc-pop-art-control-text">
      {{ $store.state.utils.configuration.enableNewUgcModal ? translations.ugcPopArtControlDesc2 : translations.ugcPopArtControlDesc }}
    </p>
  </section>
</template>

<script>
/**
 * PopArtControl component
 */

import BaseUgcTool from "../BaseUgcTool";
import ugcCropBox from '@/components/UGC/Tools/CropBox';
import popArtConfig from "./pop-art.json";
import canvasUtils from "@/plugins/canvas/CanvasUtils";

export default {
  name: "UgcPopArtControl",
  extends: BaseUgcTool,
  components: {
    ugcCropBox
  },
  data() {
    return {
      controlName: "pop-art",
      lockSquare: true
    };
  },
  computed: {
    translations() {
      return this.$store.state.translations.data;
    },
    fabricCanvas() {
      return this.$store.state.ugc.stateManager.canvas;
    },
    tempData() {
      return this.$store.state.ugc.tempData;
    },
    tempCropData() {
      const { cropper, activeUGCComponent } = this.tempData.features[this.tempData.activeUGCIndex];
      return cropper[activeUGCComponent]
        ? cropper[activeUGCComponent][this.controlName]
        : null;
    },
    imageFormat() {
      return this.tempData.features[this.tempData.activeUGCIndex].ugc_image_format;
    }
  },
  methods: {
    /**
     * @function createPopArt
     * @param imageUrl - base64 image string
     * Creates a tiled image with different tint overlays according to the config JSON and sets it in the canvas
     */
    createPopArt(imageUrl) {
      const {
        width,
        height,
        rows,
        columns,
        colors,
        filter: { alpha }
      } = popArtConfig;
      const uniqColors = [...new Set(colors)]; // Removing duplicated colors
      const blendedImages = {};
      const colorsArr = [...colors];
      // Load image string

      return canvasUtils
        .getImageFromUrl(imageUrl)
        .then(image => {
          return canvasUtils.resizeImage({ image, width, height });
        })
        .then(imageBuffer => {
          return Promise.all(
            uniqColors.map(
              color =>
                new Promise((resolve, reject) => {
                  canvasUtils
                    .applyColorOverlay({
                      imageBuffer,
                      color,
                      alpha,
                      width,
                      height
                    })
                    .then(blendedImage => {
                      blendedImages[color] = blendedImage;
                      // Resolve promise
                      resolve();
                    })
                    .catch(reject);
                })
            )
          );
        })
        .then(() => {
          const canvasWidth = columns * width;
          const canvasHeight = rows * height;
          let canvas = canvasUtils.createCanvasElement({
            width: canvasWidth,
            height: canvasHeight
          });
          const ctx = canvas.getContext("2d");
          // Create tile matrix
          for (let x = 0; x < columns; x++) {
            const left = x * width;
            for (let y = 0; y < rows; y++) {
              const top = y * height;
              const color = colorsArr.pop();
              ctx.drawImage(blendedImages[color], left, top); //draw image
            }
          }
          const tiledImage = canvas.toDataURL();
          canvas = null;
          return tiledImage;
        })
        .then(tiledImage => {
          // Set image in canvas
          return this.$store.dispatch("ugc/SET_CANVAS_IMAGE", {
            fabricCanvas: this.fabricCanvas,
            imageSrc: tiledImage
          });
        })
        .then(fabricImage => {
          // cleaning blended images
          Object.keys(blendedImages).forEach(color => {
            blendedImages[color] = null;
            delete blendedImages[color];
          });
          this.$eventBus.$emit("reload-placement-guide");
          this.$eventBus.$emit("control-finish", this.controlName);
          this.$store.commit("ugc/stateManager/APPLY_TILE_OR_POPART", true);
          return Promise.resolve(fabricImage);
        })
        .catch(console.error);

    },
    /**
     * Handler for the cancel button. It hides the cropbox and restores the previous applied image
     * @public
     */
     onControlCancelBack() {
      if (this.tempCropData) {
        const canvasImage = this.fabricCanvas.getObjects("image");
        this.$refs.cropbox.loadImageMetadata({
          image: canvasImage[0],
          data: this.currentImage
        });
      }
      this.$refs.cropbox.hide();
    },
    /**
     * Handler for the done button. It crops the canvas image and fires the pop art logic
     * @public
     */
    onControlDone() {
      const { tempData } = this.$store.state.ugc;
      const {
        activeUGCComponent: componentName
      } = tempData.features[tempData.activeUGCIndex];
      const canvasImage = this.fabricCanvas.getObjects("image");
      this.$store.commit("ugc/tempData/SAVE_CROPPER_STATE", {
        tool: this.controlName,
        componentName,
        image: this.$refs.cropbox.getImageMetadata(canvasImage[0]),
        cropBox: {
          height: this.$refs.cropbox.cropBox.height,
          left: this.$refs.cropbox.cropBox.left,
          top: this.$refs.cropbox.cropBox.top,
          width: this.$refs.cropbox.cropBox.width
        }
      });
      const croppedImage = this.$refs.cropbox.crop();
      this.$refs.cropbox.hide();
      this.createPopArt(croppedImage);
    }
  }
};
</script>
